import type { BrandFontFile, NamedVariations } from "../types";

/** The parsed-metadata subset of an uploaded file the derivations read. */
export type DerivableFontFile = Pick<
  BrandFontFile,
  | "familyName"
  | "weight"
  | "style"
  | "preferredFamily"
  | "preferredSubfamily"
  | "variationAxes"
  | "namedVariations"
>;

const WEIGHT_TO_STYLE_NAMES: Record<number, string[]> = {
  100: ["Thin"],
  200: ["ExtraLight", "Extra Light", "UltraLight", "Ultra Light"],
  300: ["Light"],
  400: ["Regular", "Normal", "Book", "Roman"],
  500: ["Medium"],
  600: ["SemiBold", "Semi Bold", "DemiBold", "Demi Bold"],
  700: ["Bold"],
  800: ["ExtraBold", "Extra Bold", "UltraBold", "Ultra Bold"],
  900: ["Black", "Heavy"],
};

function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function stripStyleSuffix(family: string, suffixes: string[]): string {
  const lower = family.toLowerCase();
  const candidates = suffixes
    .map(collapseWhitespace)
    .filter(Boolean)
    .sort((left, right) => right.length - left.length);
  for (const suffix of candidates) {
    if (lower.endsWith(` ${suffix.toLowerCase()}`)) {
      return family.slice(0, family.length - suffix.length - 1).trim();
    }
  }
  return family;
}

export function isVariableFontFile(file: DerivableFontFile): boolean {
  return Boolean(file.weight?.includes(" ") || file.variationAxes);
}

export function deriveFontFamily(file: DerivableFontFile): string | undefined {
  const preferred = file.preferredFamily?.trim();
  if (preferred) return preferred;

  const familyName = file.familyName && collapseWhitespace(file.familyName);
  if (!familyName) return undefined;

  if (isVariableFontFile(file)) {
    return stripStyleSuffix(
      familyName,
      Object.keys(file.namedVariations ?? {}),
    );
  }

  const weight = Number(file.weight);
  return stripStyleSuffix(familyName, WEIGHT_TO_STYLE_NAMES[weight] ?? []);
}

export type NamedWeightInstance = { name: string; wght: number };

export function namedVariationWeights(
  namedVariations: NamedVariations | undefined,
): NamedWeightInstance[] {
  return Object.entries(namedVariations ?? {})
    .flatMap(([name, coords]) => {
      const wght = coords?.["wght"];
      return typeof wght === "number" ? [{ name, wght }] : [];
    })
    .sort((left, right) => left.wght - right.wght);
}

export function nearestNamedVariation(
  instances: readonly NamedWeightInstance[],
  targetWeight: number,
): string | undefined {
  if (instances.length === 0) return undefined;
  return instances.reduce((best, entry) =>
    Math.abs(entry.wght - targetWeight) < Math.abs(best.wght - targetWeight)
      ? entry
      : best,
  ).name;
}

export function cssWeightToNumber(cssWeight: string): number {
  const trimmed = cssWeight.trim().toLowerCase();
  if (trimmed === "bold" || trimmed === "bolder") return 700;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 400;
}

export function weightToStyleName(weight: number): string {
  const bucket = Math.min(900, Math.max(100, Math.round(weight / 100) * 100));
  return WEIGHT_TO_STYLE_NAMES[bucket]?.[0] ?? "Regular";
}

export function styleWithItalic(base: string, italic: boolean): string {
  if (!italic || /\bitalic\b/i.test(base)) return base;
  return base === "Regular" ? "Italic" : `${base} Italic`;
}

export function deriveFigmaStyle(args: {
  file: DerivableFontFile;
  weightInstances: readonly NamedWeightInstance[];
  cssWeight: string;
  italic: boolean;
}): string {
  const { file, weightInstances, cssWeight, italic } = args;
  const requestedWeight = cssWeightToNumber(cssWeight);
  const named = nearestNamedVariation(weightInstances, requestedWeight);
  const base = named ?? styleNameForFileWeight(file, requestedWeight);
  return styleWithItalic(base, italic || file.style === "italic");
}

function styleNameForFileWeight(
  file: DerivableFontFile,
  requestedWeight: number,
): string {
  if (isVariableFontFile(file)) {
    return weightToStyleName(requestedWeight);
  }
  const preferredSubfamily = file.preferredSubfamily?.trim();
  if (preferredSubfamily) {
    return preferredSubfamily;
  }
  return weightToStyleName(faceOwnWeight(file));
}

function faceOwnWeight(file: DerivableFontFile): number {
  const wghtAxis = file.variationAxes?.["wght"];
  if (wghtAxis) return wghtAxis.default;
  const single = Number(file.weight);
  return Number.isFinite(single) ? single : 400;
}
